<template>
  <div class="landing-container relative z-[1]">
    <TopHeader class="landing-container-top" @show-user-pop="handleShowUserPop" />
    <Page class="page-enter" name="enter">
      <Banner @trial="handleTrial" @consult="handleConsult" />
      <CreateLikeAPro />
    </Page>

    <Page class="bg-[var(--x-text-1)]" name="Gen Like Typing">
      <GenLikeTyping @trial="handleTrial" />
    </Page>

    <Page class="page-avatar bg-white z-[1]" name="3D Avatar">
      <AvatarFeature @trial="handleTrial" />
      <ChooseFavoriteCharacter />
    </Page>

    <Page class="page-video bg-[var(--x-text-1)]" name="AI Video">
      <VideoFeature @trial="handleTrial" />
    </Page>

    <Page class="page-detail bg-[var(--x-white-1)]" name="control detail">
      <ControlEveryDetail @trial="handleTrial" />
    </Page>

    <Page class="page-create bg-white relative" name="create">
      <WhatMakesRendoraSoPowerful />
    </Page>

    <Page class="bg-[var(--x-back-3)]">
      <Scenarios />
    </Page>

    <Page class="bg-[var(--x-text-1)]">
      <DataShowcase />
    </Page>

    <Page class="bg-var(--x-back-3)">
      <Faq />
    </Page>

    <Page>
      <StartRightNow :handler="handleTrial" />
      <AppFooter />
    </Page>
    <MobileBottomUserPop ref="mobileUserRef" />
  </div>
</template>
<script lang="ts" setup>
import { PositionCode, useContactUs } from '@rendora/common/features/contact';
import { useBreakpoint } from '@rendora/design/index';
import MobileBottomUserPop from '~/design/widgets/mobile-bottom-user-pop/index.vue';
import TopHeader from '~/design/widgets/top-header/index.vue';

import { transformImage } from '~/utils/';
import { getFeUrl } from '~/utils/url';
import Page from './components/page.vue';
// Banner 必须保持静态引入，因为它是首屏 LCP 的关键
import Banner from './modules/banner/index.vue';

const headers = useRequestHeaders();
const { isMobile } = useBreakpoint(headers?.['user-agent']);

// 初始化视频自动播放 Hook（微信环境优化）
const videoAutoplay = useGlobalVideoAutoplay();

// --- 优化: 将非首屏组件改为异步加载 (Code Splitting) ---
// 底部组件
const AppFooter = defineAsyncComponent(
  () => import('~/design/widgets/bottom-footer/footer-cn.vue'),
);
const Faq = defineAsyncComponent(() => import('~/design/widgets/faq/cn.vue'));
const StartRightNow = defineAsyncComponent(
  () => import('~/design/widgets/start-right-now/start-cn.vue'),
);

// 页面模块组件
const AvatarFeature = defineAsyncComponent(() => import('./modules/avatar-feature/index.vue'));
const ChooseFavoriteCharacter = defineAsyncComponent(
  () => import('./modules/choose-favorite-character/index.vue'),
);
const ControlEveryDetail = defineAsyncComponent(
  () => import('./modules/control-every-detail/index.vue'),
);
const CreateLikeAPro = defineAsyncComponent(() => import('./modules/create-like-a-pro/index.vue'));
const DataShowcase = defineAsyncComponent(() => import('~/design/widgets/data-showcase/index.vue'));
const Scenarios = defineAsyncComponent(() => import('./modules/scenarios/index.vue'));
const VideoFeature = defineAsyncComponent(() => import('./modules/video-feature/index.vue'));
const WhatMakesRendoraSoPowerful = defineAsyncComponent(
  () => import('./modules/what-makes-rendora-so-powerful/index.vue'),
);

const GenLikeTyping = defineAsyncComponent(
  () => import('~/design/widgets/gen-like-typing/index-cn.vue'),
);

const mobileUserRef = ref<InstanceType<typeof MobileBottomUserPop> | null>(null);

const handleTrial = () => {
  // SSR 环境下不执行
  if (import.meta.server) return;

  const userInfo = useUserInfo();
  if (!userInfo.value) {
    navigateTo(
      `/login?redirect=${encodeURIComponent(encodeURIComponent(`${location.origin}/workspace`))}`,
    );
  } else {
    if (isMobile.value) {
      handleShowUserPop();
      return;
    }
    location.href = '/workspace';
  }
};

const handleConsult = () => {
  useContactUs(PositionCode['有言官网-企业咨询'], '企业需求咨询', '扫码加行业专家·快速响应');
};

const handleShowUserPop = () => {
  if (isMobile.value) {
    mobileUserRef.value?.open();
  }
};

const link = transformImage(getFeUrl('/rendora/main/banner/bg3.png'));

useHead({
  link: [
    {
      rel: 'preload',
      href: link,
      as: 'image',
      fetchpriority: 'high',
      type: 'image/webp',
    },
  ],
});

onBeforeMount(() => {
  // 重置滚动位置（仅在客户端）
  if (import.meta.client && typeof window !== 'undefined') {
    window.scrollTo(0, 0);
  }
});

onMounted(() => {
  // 设置视频自动播放解锁监听器（仅需一次，用于 iOS 微信环境）
  videoAutoplay.setupUnlockListeners();
});
</script>
<style lang="scss" scoped src="./style/mobile.scss"></style>
<style lang="scss" scoped src="./style/pc.scss"></style>
